import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { toast } from 'react-toastify';
import BadgeWrapper from './BadgeWrapper';
import Badge from '../../../v2/MUI_custom_component/Badge';
import { QuantityHandler } from '../../Components';
import IconReusable from '../../../components/IconReusable';
import { useSelector } from 'react-redux';

const ProductCard = ({
  productName = '',
  availableQty = null,
  unverifiedQty = null,
  imageUrl = '',
  quantity = 0,
  onQuantityChange,
  onIncreasePress,
  onReducePress,
  onTextChange,
  showQuantityControls = false,
  price = null,
  badgeText = null,
  showDeleteButton = false,
  onDelete,
  totalQuantity = '',
  qtyText = '',
  row = null,
  imageWidth = {xs: 70, sm: 98},
  imageHeight = {xs: 70, sm: 98},
  maxQuantity,
}) => {
  const isMobile = useMediaQuery('(max-width:1200px)');
  const showHistory = useSelector(state => state.entityReducer.show_history);
    
  
  // State management similar to CreateDp ProductCard
  const [inputValue, setInputValue] = useState(quantity.toString());
  
  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);
  
  // Utility function to fix floating point precision issues
  const roundToThreeDecimals = (num) => {
    return Math.round((num + Number.EPSILON) * 1000) / 1000;
  };

  const handleIncrease = () => {
    // Always call parent's validation logic - let parent handle validation and toast messages
    if (onIncreasePress) onIncreasePress();
  };

  const handleDecrease = () => {
    if (onReducePress) onReducePress();
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d{0,3}$/.test(value)) {
      setInputValue(value);
      if (onTextChange) {
        // Pass both event and direct value for compatibility
        onTextChange(event);
      }
    }
  };

  const handleInputBlur = () => {
    const numericValue = parseFloat(inputValue);
    if (isNaN(numericValue) || numericValue < 0) {
      setInputValue('0');
      if (onQuantityChange) onQuantityChange(0);
    } else {
      const roundedValue = roundToThreeDecimals(numericValue);
      setInputValue(roundedValue.toString());
      if (onQuantityChange) onQuantityChange(roundedValue);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleInputBlur();
    }
  };
    
  return (
    <BadgeWrapper backgroundColor='transparent' >
      {badgeText ? (
          <Badge
            badgeText={badgeText}
            badgeType="list"
            badgeSize="small"
          />
        ) : null
      }
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          pt: { xs: badgeText ? 2: 1, md:2 },
          position: 'relative',
          justifyContent: { xs:'space-between', md: 'start' },
          borderTop: '1px solid #E0E0E0',
        }}
      >
        
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems:{ xs :'flex-start', md:'center' },
          gap:{ xs:2, sm: 2 },
          pt: { xs: badgeText ? 2: 1, md:0 },
          position: 'relative',
          width: {xs: '100%', md: '50%'},
          backgroundColor: 'transparent',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={productName}
          sx={{
            width: imageWidth,
            height: imageHeight,
            borderRadius: 1,
            objectFit: 'cover',
            justifyContent: { xs:'space-between', md: 'center' },
          }}
        />
          <Box>
            <Typography variant="body1" sx={{ mb: { xs: .5, sm: 1.5 }, fontSize: { xs: 13, sm: 16 }, fontWeight:700, lineHeight: {xs: '20px', sm: '24px'} }}>
              {productName}
            </Typography>
            {price && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                ₹{price}
              </Typography>
            )}
            {availableQty !== null && (
              <Box sx={{ display: { md: 'flex' }, flexDirection: { xs: 'row' } }}>
                <Typography variant="body2" sx={{ color: '#8C8C8C'}}>
                  Available Qty: 
                </Typography>
                <Typography variant="body2" sx={{ color: '#333', ml: {md: 0.4} }}>{availableQty}</Typography>
              </Box>
            
            )}
            {unverifiedQty !== null  && (
              <Typography variant="body2" sx={{ color: '#8C8C8C' }}>
                Unverified Qty: {unverifiedQty}
              </Typography>
            )}
            {totalQuantity  && (
              <Typography variant="body2" sx={{ color: '#8C8C8C' }}>
                Total Qty: {totalQuantity}
              </Typography>
            )}
          </Box>
        </Box>
        {qtyText && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto', alignSelf: { xs: 'flex-end', md: 'flex-end' }, whiteSpace: 'nowrap' }}>
            <Typography variant="labelM"   sx={{ color: '#8C8C8C', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Total Qty.
            </Typography>
            <Typography variant="p3" sx={{ color: '#333' }}>
              {qtyText}
            </Typography>
          </Box>
        )}
        {(showDeleteButton || showQuantityControls) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', md: 'row' },
              alignItems:'center',
              width: { xs: 'auto', md: '50%' },
              justifyContent: 'space-between',
              alignSelf: { xs: showDeleteButton ? 'stretch': 'end', md: 'center'},
            }}
          >
            {showQuantityControls && (onQuantityChange || (onIncreasePress && onReducePress)) && (
              <QuantityHandler
                value={inputValue}
                onIncrease={handleIncrease}
                onDecrease={handleDecrease}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyPress={handleKeyPress}
                variant={isMobile ? "small" : "medium"}
                increaseDisabled={maxQuantity !== undefined && parseFloat(inputValue) >= maxQuantity}
                onIncreaseAttempt={handleIncrease}
              />
            )}
            {showDeleteButton && onDelete && (
              <IconButton 
                sx={{ 
                  order: { xs: 1, md: 2 }, 
                  color: !showHistory ? '#995A00' : '#E5E5E5', 
                  alignSelf: { xs: 'end' } 
                }}
                disabled={showHistory}
                onClick={onDelete}>
                <IconReusable name='Delete' color='#995A00' size={isMobile ? '20px' : '24px'} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </BadgeWrapper>
  );
};

export default ProductCard;
